import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

import { DEFAULT_APP_LOCALE } from '../i18n/app-locale';
import { AuthStateService } from '../services/auth-state.service';
import { PreferredLanguageService } from '../services/preferred-language.service';

/**
 * Returns a guard result that sends a signed-out client to `/auth` — never
 * to `/en/auth` (CLP-951).
 *
 * Call it in the guard's injection context, before any `pipe`; the returned
 * function can then be used inside operators. In the German bundle it is the
 * usual `UrlTree`. In any other bundle a `UrlTree` would stay under that
 * bundle's base href, so it starts a document load of `/auth` through
 * {@link AuthStateService.navigateToLogin} and cancels this navigation.
 */
export function injectLoginRedirect(): () => UrlTree | false {
  const router = inject(Router);
  const authState = inject(AuthStateService);
  const inSourceLocale = inject(PreferredLanguageService).active === DEFAULT_APP_LOCALE;

  return () => {
    if (inSourceLocale) {
      return router.createUrlTree(['/auth']);
    }
    authState.navigateToLogin();
    return false;
  };
}

/**
 * On the `auth` route: the login page is only served at `/auth`, so opening
 * `/en/auth` directly moves to it. A no-op in the German bundle, and for a
 * signed-in client, whom `unauthGuard` sends on to the dashboard in place.
 */
export const sourceLocaleLoginGuard: CanActivateFn = () => {
  if (inject(PreferredLanguageService).active === DEFAULT_APP_LOCALE) {
    return true;
  }
  if (localStorage.getItem('accessToken')) {
    return true;
  }
  inject(AuthStateService).navigateToLogin();
  return false;
};
